import { heartbeatAgeSeconds, heartbeatThresholdSeconds, healthFromAgentDocument } from '../../../utils/agentHealthModel';

const ONLINE_STATUSES = new Set(['online', 'healthy', 'connected']);
const OFFLINE_STATUSES = new Set(['offline', 'error', 'stale', 'never', 'provisioned']);
/** Offline agents older than this are treated as stale (~6 months). */
export const STALE_OFFLINE_MS = 180 * 24 * 60 * 60 * 1000;
const DESC_DEFAULT = new Set(['online', 'lastHeartbeat']);
const SEGMENT_DESC_DEFAULT = new Set(['status', 'standbyCount', 'devicesCount']);
const STATUS_RANK = { green: 3, degraded: 2, red: 1 };

export function getAgentDisplayName(agent) {
  const name = String(agent?.name || agent?.agentName || agent?.displayName || '').trim();
  return name;
}

export function extractDisplayCidr(raw) {
  const text = String(raw || '').trim();
  if (!text) return '';
  if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\/\d{1,2}$/.test(text)) return text;
  const underscored = text.match(/(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})_(\d{1,2})$/);
  if (underscored) return `${underscored[1]}/${underscored[2]}`;
  const slash = text.match(/(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})\/(\d{1,2})/);
  if (slash) return `${slash[1]}/${slash[2]}`;
  return text;
}

export function getSegmentDisplayCidr(segment) {
  return extractDisplayCidr(segment?.cidr || segment?.segmentId);
}

export function formatAgentLanIdentity(agent, fallbackCidr = '') {
  const ipv4 = String(agent?.lanIpv4 || agent?.ip || agent?.localIp || '').trim();
  const cidr = extractDisplayCidr(agent?.segment || agent?.segmentId) || extractDisplayCidr(fallbackCidr);
  if (ipv4 && cidr) return `${ipv4} · ${cidr}`;
  if (ipv4) return ipv4;
  if (cidr) return cidr;
  return '';
}

export function getPrimaryDisplayName(segment, agents = []) {
  const fromList = (agents || []).find((a) => a.agentId === segment?.primaryAgentId);
  const fromSegment = (segment?.agents || []).find((a) => a.agentId === segment?.primaryAgentId);
  return getAgentDisplayName(fromList) || getAgentDisplayName(fromSegment);
}

export function agentBelongsToSegment(agent, segment) {
  if (!agent?.agentId || !segment) return false;
  const agentCidr = extractDisplayCidr(agent.segment || agent.segmentId);
  const segmentCidr = getSegmentDisplayCidr(segment);
  if (agentCidr && segmentCidr && agentCidr !== segmentCidr) return false;
  if (agent.agentId === segment.primaryAgentId) return true;
  if (Array.isArray(segment.agents) && segment.agents.some((a) => a.agentId === agent.agentId)) {
    return true;
  }
  return Boolean(agentCidr && segmentCidr && agentCidr === segmentCidr);
}

export function scanDisabledReason(agent, segment) {
  if (!isAgentOnline(agent)) return 'Agent is offline — cannot scan';
  if (!agentBelongsToSegment(agent, segment)) {
    const agentCidr = extractDisplayCidr(agent.segment || agent.segmentId);
    const segmentCidr = getSegmentDisplayCidr(segment);
    if (agentCidr && segmentCidr && agentCidr !== segmentCidr) {
      return `This agent is on ${agentCidr}, not ${segmentCidr}`;
    }
    return 'This agent is not assigned to this segment';
  }
  return '';
}

export function apiErrorMessage(error, fallback = 'Request failed') {
  const data = error?.response?.data;
  if (typeof data?.error === 'string' && data.error) return data.error;
  if (typeof data?.error?.message === 'string' && data.error.message) return data.error.message;
  if (typeof data?.message === 'string' && data.message) return data.message;
  if (typeof error?.message === 'string' && error.message) return error.message;
  return fallback;
}

export function toMillis(value) {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return value > 1e12 ? value : value * 1000;
  if (typeof value === 'object') {
    if (typeof value._seconds === 'number') return value._seconds * 1000;
    if (typeof value.seconds === 'number') return value.seconds * 1000;
    if (typeof value.toDate === 'function') {
      try {
        const date = value.toDate();
        return date instanceof Date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
      } catch {
        return 0;
      }
    }
  }
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
}

export function heartbeatMs(agent) {
  return toMillis(
    agent?.lastHeartbeat || agent?.lastHeartbeatAt || agent?.last_heartbeat || agent?.lastSeen
  );
}

export function hasAgentHeartbeat(agent) {
  return heartbeatMs(agent) > 0;
}

export function isAgentOnline(agent, now = new Date()) {
  const status = String(agent?.status || '').toLowerCase();
  if (OFFLINE_STATUSES.has(status)) return false;
  const moment = now instanceof Date ? now : new Date(now);
  const age = heartbeatAgeSeconds(
    agent?.lastHeartbeat || agent?.lastHeartbeatAt || agent?.last_heartbeat || agent?.lastSeen,
    moment
  );
  if (age == null || age > heartbeatThresholdSeconds(agent?.expectedHeartbeatSeconds)) return false;
  if (ONLINE_STATUSES.has(status)) return true;
  return true;
}

export function segmentsIncludingLiveNetworks(segments, agents = []) {
  const base = (segments || []).map((segment) => {
    const segmentCidr = getSegmentDisplayCidr(segment);
    const primary = (agents || []).find((agent) => agent?.agentId === segment?.primaryAgentId);
    const primaryCidr = extractDisplayCidr(primary?.segment || primary?.segmentId);
    if (segmentCidr && primaryCidr && primaryCidr !== segmentCidr) {
      return { ...segment, primaryAgentId: '', status: 'red', agents: [] };
    }
    return segment;
  });
  const known = new Set(base.map((segment) => getSegmentDisplayCidr(segment)).filter(Boolean));
  const extras = [];
  const seen = new Set();
  (agents || []).forEach((agent) => {
    const cidr = extractDisplayCidr(agent?.segment || agent?.segmentId);
    const status = String(agent?.status || '').toLowerCase();
    if (!cidr || known.has(cidr) || seen.has(cidr) || OFFLINE_STATUSES.has(status)) return;
    seen.add(cidr);
    const onSubnet = (agents || []).filter((candidate) => (
      extractDisplayCidr(candidate?.segment || candidate?.segmentId) === cidr
      && !OFFLINE_STATUSES.has(String(candidate?.status || '').toLowerCase())
    ));
    const primary = onSubnet[0];
    extras.push({
      segmentId: cidr,
      cidr,
      status: 'green',
      primaryAgentId: primary?.agentId || '',
      agents: onSubnet.map((candidate) => ({
        agentId: candidate.agentId,
        role: candidate.agentId === primary?.agentId ? 'primary' : 'pool',
      })),
      devicesCount: 0,
      standbyCount: Math.max(0, onSubnet.length - 1),
    });
  });
  return base.concat(extras);
}

export function segmentStatusPresentation(segment, agents = [], now = new Date()) {
  const moment = now instanceof Date ? now : new Date(now);
  const relevant = (agents || []).filter((agent) => agentBelongsToSegment(agent, segment));
  if (relevant.length === 0) {
    const segmentCidr = getSegmentDisplayCidr(segment);
    const primary = (agents || []).find((agent) => agent?.agentId && agent.agentId === segment?.primaryAgentId);
    const primaryCidr = extractDisplayCidr(primary?.segment || primary?.segmentId);
    if (segmentCidr && primaryCidr && primaryCidr !== segmentCidr) {
      return { label: 'Offline', color: 'error' };
    }
  }
  if (relevant.length > 0 && relevant.every((agent) => {
    const state = healthFromAgentDocument(agent, moment).state;
    return state === 'offline' || state === 'dormant';
  })) {
    return { label: 'Offline', color: 'error' };
  }
  const primary = relevant.find((agent) => agent?.agentId === segment?.primaryAgentId);
  const primaryState = primary ? healthFromAgentDocument(primary, moment).state : '';
  if (primaryState === 'degraded' || primaryState === 'starting') {
    return { label: 'Degraded', color: 'warning' };
  }
  const key = segment?.status;
  const labels = { green: 'Routing healthy', degraded: 'Degraded', red: 'Offline' };
  const colors = { green: 'success', degraded: 'warning', red: 'error' };
  return { label: labels[key] || key || 'Unknown', color: colors[key] || 'default' };
}

export function isAgentPrimaryForSegment(agent, segment) {
  if (!agent?.agentId || !segment) return false;
  if (agent.agentId === segment.primaryAgentId) return true;
  return Boolean(
    segment.agents?.some(
      (row) => row.agentId === agent.agentId && row.role === 'primary'
    )
  );
}

/** Offline, not primary for this segment — collapsed by default in segment agent lists. */
export function isStaleSegmentAgent(agent, segment) {
  if (!agent || !segment) return false;
  if (isAgentOnline(agent)) return false;
  if (isAgentPrimaryForSegment(agent, segment)) return false;
  return true;
}

/**
 * Agents hidden by default in unified lists: never heartbeated ghosts, offline agents
 * (including stale primaries — reassign from Network Segments), and long-idle records.
 */
export function isDismissibleStaleAgent(agent, segments) {
  if (!agent?.agentId) return true;
  const explicit = String(agent.effectiveHealth || agent.diagnostics?.effectiveHealth || '').toLowerCase();
  if (explicit === 'online' || explicit === 'degraded' || explicit === 'starting' || explicit === 'dormant') return false;
  if (!hasAgentHeartbeat(agent)) return true;
  if (isAgentOnline(agent)) return isGloballyStaleAgent(agent);
  const seen = heartbeatMs(agent);
  if (seen && Date.now() - seen <= 90 * 1000) return false;
  return true;
}

export function agentKindLabel(agent) {
  const kind = String(agent?.diagnostics?.agentType || agent?.runtimeClass || agent?.formFactor || agent?.agentType || '').toLowerCase();
  if (kind === 'workstation' || kind === 'desktop' || kind === 'standalone') return 'Workstation';
  if (kind.includes('panel') || kind.includes('touch')) return 'Touch panel';
  if (kind.includes('embedded') || kind.includes('esp')) return 'Embedded';
  return kind ? kind : 'Agent';
}

/** Global stale: offline with no heartbeat or last seen beyond STALE_OFFLINE_MS. */
export function isGloballyStaleAgent(agent) {
  if (isAgentOnline(agent)) return false;
  const ms = heartbeatMs(agent);
  if (!ms) return true;
  return Date.now() - ms >= STALE_OFFLINE_MS;
}

export function formatOfflineAge(agent) {
  const ms = heartbeatMs(agent);
  if (!ms) return 'No heartbeat';
  const days = Math.floor((Date.now() - ms) / 86400000);
  if (days >= 180) return `Offline · ${Math.floor(days / 30)} mo`;
  if (days >= 30) return `Offline · ${Math.floor(days / 30)} mo`;
  if (days >= 1) return `Offline · ${days}d`;
  return 'Offline · today';
}

export function getAgentFormFactor(agent) {
  return String(agent?.formFactor || agent?.form_factor || '').toLowerCase();
}

export function isEmbeddedFormFactor(formFactor) {
  const ff = String(formFactor || '').toLowerCase();
  return ff === 'embedded_agent' || ff === 'embedded';
}

function isPanelLikeAgent(agent) {
  const ff = getAgentFormFactor(agent);
  if (ff === 'panel' || ff === 'touch_panel' || ff === 'touchpanel' || ff === 'appliance') {
    return true;
  }
  const id = String(agent?.agentId || '');
  return id.startsWith('agent-panel-');
}

/** Headless edge hardware (ESP32-P4, etc.) — not LAN segment scanners or touch panels. */
export function isEmbeddedEdgeAgent(agent) {
  if (!agent?.agentId) return false;
  if (isPanelLikeAgent(agent)) return false;
  if (isEmbeddedFormFactor(getAgentFormFactor(agent))) return true;
  if (String(agent.agentId).startsWith('agent-embedded-')) return true;
  const platform = String(agent?.platform || '').toLowerCase();
  const hasSegment = Boolean(extractDisplayCidr(agent?.segment || agent?.segmentId));
  if ((platform === 'esp32p4' || platform === 'esp32') && !hasSegment) return true;
  return false;
}

export function isAgentAssignedToAnySegment(agent, segments) {
  return (segments || []).some((segment) => agentBelongsToSegment(agent, segment));
}

/** Embedded/edge agents for the tenant that are not listed under a network segment. */
export function listEmbeddedEdgeAgents(allAgents, segments) {
  return (allAgents || []).filter(
    (agent) => isEmbeddedEdgeAgent(agent) && !isAgentAssignedToAnySegment(agent, segments)
  );
}

/** True when the agent should render as an embedded row (not a LAN workstation row). */
export function isEmbeddedEdgeRow(agent, segments) {
  return isEmbeddedEdgeAgent(agent) && !isAgentAssignedToAnySegment(agent, segments);
}

/** LAN agents first, embedded edge agents last — for combined superadmin view. */
export function listCombinedLanAndEmbeddedAgents(lanAgents, embeddedAgents) {
  return [...(lanAgents || []), ...(embeddedAgents || [])];
}

/** Workstation / LAN agents (segment scanners, desktops) — excludes embedded edge rows. */
export function listLanWorkstationAgents(allAgents, segments) {
  return (allAgents || []).filter((agent) => {
    if (!agent?.agentId) return false;
    return !(isEmbeddedEdgeAgent(agent) && !isAgentAssignedToAnySegment(agent, segments));
  });
}

export function findSegmentForAgent(agent, segments) {
  if (!agent?.agentId) return null;
  return (segments || []).find((segment) => agentBelongsToSegment(agent, segment)) || null;
}

export function getEmbeddedPlatformLabel(agent) {
  const platform = String(agent?.platform || '').trim();
  const formFactor = getAgentFormFactor(agent);
  if (platform && formFactor && platform.toLowerCase() !== formFactor) {
    return `${platform} · ${formFactor}`;
  }
  return platform || formFactor || agent?.runtimeClass || '—';
}

export function listBulkDeletableAgents(allAgents, segments) {
  const primaryIds = new Set(
    (segments || [])
      .map((seg) => seg?.primaryAgentId)
      .filter(Boolean)
  );
  return (allAgents || []).filter(
    (agent) =>
      agent?.agentId &&
      !primaryIds.has(agent.agentId) &&
      !isEmbeddedEdgeAgent(agent) &&
      (!hasAgentHeartbeat(agent) || !isAgentOnline(agent))
  );
}

export function nextAgentSort(current, key) {
  if (current?.key === key) {
    return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  }
  return { key, dir: DESC_DEFAULT.has(key) ? 'desc' : 'asc' };
}

export function nextSegmentSort(current, key) {
  if (current?.key === key) {
    return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  }
  return { key, dir: SEGMENT_DESC_DEFAULT.has(key) ? 'desc' : 'asc' };
}

function compareText(a, b) {
  return String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base', numeric: true });
}

export function compareAgents(left, right, sortBy, direction = 'asc') {
  const dir = direction === 'asc' ? 1 : -1;
  let cmp = 0;

  switch (sortBy) {
    case 'online':
      cmp = Number(isAgentOnline(left)) - Number(isAgentOnline(right));
      if (cmp === 0) cmp = heartbeatMs(left) - heartbeatMs(right);
      break;
    case 'name':
      cmp = compareText(getAgentDisplayName(left), getAgentDisplayName(right));
      if (cmp === 0) cmp = compareText(left?.agentId, right?.agentId);
      break;
    case 'agentId':
      cmp = compareText(left?.agentId, right?.agentId);
      break;
    case 'site':
      cmp = compareText(left?.siteLabel || left?.siteId, right?.siteLabel || right?.siteId);
      break;
    case 'segment':
      cmp = compareText(left?.segment || left?.segmentId, right?.segment || right?.segmentId);
      break;
    case 'quality':
      cmp = compareText(left?.quality, right?.quality);
      break;
    case 'lastHeartbeat':
      cmp = heartbeatMs(left) - heartbeatMs(right);
      break;
    case 'runtimeClass':
      cmp = compareText(left?.runtimeClass, right?.runtimeClass);
      break;
    case 'deviceId':
      cmp = compareText(left?.deviceId, right?.deviceId);
      break;
    case 'platform':
      cmp = compareText(getEmbeddedPlatformLabel(left), getEmbeddedPlatformLabel(right));
      break;
    case 'tenant':
      cmp = compareText(left?.tenantName || left?.tenantId, right?.tenantName || right?.tenantId);
      break;
    case 'kind':
      cmp = compareText(left?.embeddedRow ? 'embedded' : 'lan', right?.embeddedRow ? 'embedded' : 'lan');
      break;
    default:
      cmp = compareText(left?.agentId, right?.agentId);
  }

  return cmp * dir;
}

/**
 * The installed runtime heartbeats every 5 minutes. The 30-second health
 * window marks that agent offline between check-ins, so a scan would skip it.
 * Keep the agent scannable for one full check-in plus a short grace period.
 */
export function scanHeartbeatLimitSeconds(agent) {
  const expected = Number(agent?.expectedHeartbeatSeconds);
  const expectedSeconds = Number.isFinite(expected) && expected > 0 ? expected : 30;
  const checkInSeconds = Math.max(300, expectedSeconds);
  return checkInSeconds + expectedSeconds * 2;
}

export function agentCanRunScan(agent, now = new Date()) {
  const status = String(agent?.status || '').toLowerCase();
  if (OFFLINE_STATUSES.has(status)) return false;
  if (agent?.deleted || agent?.revokedAt || agent?.disabled === true || agent?.enabled === false) return false;
  const age = heartbeatAgeSeconds(
    agent?.lastHeartbeat || agent?.lastHeartbeatAt || agent?.last_heartbeat || agent?.lastSeen,
    now instanceof Date ? now : new Date(now),
  );
  if (age == null || age > scanHeartbeatLimitSeconds(agent)) return false;
  return true;
}

/** One reachable agent per subnet the agents are actually on. */
export function liveScanTargets(agents, now = new Date()) {
  const byCidr = new Map();
  (agents || []).forEach((agent) => {
    if (!agentCanRunScan(agent, now)) return;
    const cidr = extractDisplayCidr(agent?.segment || agent?.segmentId);
    const agentId = agent?.agentId;
    if (!cidr || !agentId || byCidr.has(cidr)) return;
    byCidr.set(cidr, {
      cidr,
      agentId,
      name: agent?.name || agentId,
    });
  });
  return [...byCidr.values()];
}

export function sortAgents(agents, sortBy = 'online', direction = 'desc') {
  return [...(agents || [])].sort((a, b) => compareAgents(a, b, sortBy, direction));
}

export function sortSegments(segments, sortBy = 'cidr', direction = 'asc', agents = []) {
  return [...(segments || [])].sort((left, right) => {
    const dir = direction === 'asc' ? 1 : -1;
    let cmp = 0;
    switch (sortBy) {
      case 'status':
        cmp = (STATUS_RANK[left?.status] || 0) - (STATUS_RANK[right?.status] || 0);
        break;
      case 'primary':
        cmp = compareText(
          getPrimaryDisplayName(left, agents) || left?.primaryAgentId,
          getPrimaryDisplayName(right, agents) || right?.primaryAgentId
        );
        break;
      case 'quality':
        cmp = compareText(left?.primaryAgentQuality, right?.primaryAgentQuality);
        break;
      case 'standbyCount':
        cmp = (left?.standbyCount || 0) - (right?.standbyCount || 0);
        break;
      case 'devicesCount':
        cmp = (left?.devicesCount || 0) - (right?.devicesCount || 0);
        break;
      case 'cidr':
      default:
        cmp = compareText(getSegmentDisplayCidr(left), getSegmentDisplayCidr(right));
    }
    return cmp * dir;
  });
}
